import { openDatabase } from '@/core/persistence/db'
import {
  InvalidRecentEditError,
  PersistenceError,
  PersistenceReadError,
  PersistenceWriteError,
} from '@/core/persistence/errors'
import { recentEditIdsToEvict } from '@/core/persistence/eviction'
import { assertRecentEditRecord } from '@/core/persistence/serialize'
import { StoreName, type RecentEditRecord } from '@/core/persistence/types'

function requestResult<T>(request: IDBRequest<T>, write: boolean): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(write
      ? new PersistenceWriteError(request.error)
      : new PersistenceReadError(undefined, request.error))
  })
}

function transactionComplete(transaction: IDBTransaction, write: boolean): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onabort = () => reject(write
      ? new PersistenceWriteError(transaction.error)
      : new PersistenceReadError(undefined, transaction.error))
    transaction.onerror = () => reject(write
      ? new PersistenceWriteError(transaction.error)
      : new PersistenceReadError(undefined, transaction.error))
  })
}

async function withStore<T>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => Promise<T>,
): Promise<T> {
  const database = await openDatabase()
  const write = mode === 'readwrite'
  try {
    const transaction = database.transaction(StoreName.RecentEdits, mode)
    const result = await operation(transaction.objectStore(StoreName.RecentEdits))
    await transactionComplete(transaction, write)
    return result
  } catch (error) {
    if (error instanceof PersistenceError) throw error
    throw write ? new PersistenceWriteError(error) : new PersistenceReadError(undefined, error)
  } finally {
    database.close()
  }
}

function validateStoredRecord(value: unknown): RecentEditRecord {
  try {
    assertRecentEditRecord(value)
    return value
  } catch (error) {
    if (error instanceof InvalidRecentEditError
      && typeof value === 'object'
      && value !== null
      && 'id' in value
      && typeof value.id === 'string'
      && value.id.trim()) {
      throw new InvalidRecentEditError(error.detail, value.id)
    }
    throw error
  }
}

export async function putRecentEdit(record: RecentEditRecord): Promise<void> {
  assertRecentEditRecord(record)
  await withStore('readwrite', async (store) => {
    await requestResult(store.put(record), true)
  })
}

export async function getRecentEdit(id: string): Promise<RecentEditRecord | null> {
  if (!id.trim()) throw new PersistenceReadError('A recent-edit id is required.')
  return withStore('readonly', async (store) => {
    const value: unknown = await requestResult(store.get(id), false)
    if (value === undefined) return null
    return validateStoredRecord(value)
  })
}

export async function listRecentEdits(): Promise<RecentEditRecord[]> {
  return withStore('readonly', async (store) => {
    const values = await requestResult(store.getAll() as IDBRequest<unknown[]>, false)
    return values.map(validateStoredRecord).sort((first, second) => second.updatedAt - first.updatedAt)
  })
}

export async function deleteRecentEdit(id: string): Promise<void> {
  if (!id.trim()) throw new PersistenceWriteError(new Error('A recent-edit id is required.'))
  await withStore('readwrite', async (store) => {
    await requestResult(store.delete(id), true)
  })
}

export async function clearRecentEdits(): Promise<void> {
  await withStore('readwrite', async (store) => {
    await requestResult(store.clear(), true)
  })
}

export async function markRecentEditClosed(id: string): Promise<void> {
  const record = await getRecentEdit(id)
  if (!record) throw new PersistenceReadError(`Saved edit "${id}" was not found.`)
  if (record.isOpen) await putRecentEdit({ ...record, isOpen: false })
}

export async function markAllClosed(): Promise<void> {
  const records = await listRecentEdits()
  for (const record of records) {
    if (record.isOpen) await putRecentEdit({ ...record, isOpen: false })
  }
}

export async function evictBeyondLimit(): Promise<void> {
  const records = await listRecentEdits()
  for (const id of recentEditIdsToEvict(records)) await deleteRecentEdit(id)
}
